import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDay, hasAnyRole } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, MapPin, Users as UsersIcon, Trash2, CheckSquare, X } from "lucide-react";
import OrderFormDialog from "@/components/OrderFormDialog";
import { toast } from "sonner";

export default function OrdersPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({}); // {orderId: true}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filter) params.status_filter = filter;
      const r = await api.get("/orders", { params });
      setOrders(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const canCreate = hasAnyRole(user, ["admin", "operations"]);
  const isAdmin = hasAnyRole(user, ["admin"]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const exitSelectMode = () => { setSelectMode(false); setSelected({}); };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} order(s)? This cannot be undone.`)) return;
    try {
      const r = await api.post("/orders/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${r.data.deleted} order(s)`);
      exitSelectMode();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="orders-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">All Events</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Orders</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && !selectMode && (
            <Button onClick={() => setSelectMode(true)} data-testid="select-orders-btn" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
              <CheckSquare className="w-4 h-4 mr-2" /> Select
            </Button>
          )}
          {isAdmin && selectMode && (
            <>
              <Button onClick={() => setSelected(Object.fromEntries(orders.map((o) => [o.id, true])))} data-testid="select-all-btn" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
                Select all ({orders.length})
              </Button>
              <Button onClick={bulkDelete} disabled={selectedIds.length === 0} data-testid="bulk-delete-btn" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-300 rounded-sm">
                <Trash2 className="w-4 h-4 mr-2" /> Delete selected ({selectedIds.length})
              </Button>
              <Button onClick={exitSelectMode} data-testid="cancel-select-btn" variant="outline" className="border-[#2a2a2a] rounded-sm">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </>
          )}
          {canCreate && !selectMode && (
            <Button onClick={() => setShowForm(true)} data-testid="new-order-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> New Order
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by name, location, tracking code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="orders-search"
            className="pl-9 bg-[#0a0a0a] border-[#2a2a2a] h-11 rounded-sm focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="status-filter"
          className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-3 h-11 text-sm focus:outline-none focus:border-[#D4AF37]"
        >
          <option value="">All statuses</option>
          <option value="order_confirmed">Order Confirmed</option>
          <option value="kitchen_preparing">Kitchen Preparing</option>
          <option value="driver_assigned">Driver Assigned</option>
          <option value="on_the_way">On The Way</option>
          <option value="setup_in_progress">Setup In Progress</option>
          <option value="event_ready">Event Ready</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && <div className="col-span-full text-center text-zinc-600 py-10 text-sm">Loading orders…</div>}
        {!loading && orders.length === 0 && <div className="col-span-full text-center text-zinc-600 py-10 text-sm">No orders found.</div>}
        {orders.map((o) => (
          <Link
            key={o.id}
            to={`/orders/${o.id}`}
            data-testid={`order-card-${o.id}`}
            onClick={(e) => { if (selectMode) { e.preventDefault(); toggleSelect(o.id); } }}
            className={`card-lux p-5 flex flex-col gap-3 hover:-translate-y-0.5 transition-transform relative ${selectMode && selected[o.id] ? "ring-1 ring-red-500/60" : ""}`}
          >
            {selectMode && (
              <input
                type="checkbox"
                checked={!!selected[o.id]}
                onChange={() => toggleSelect(o.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`order-select-${o.id}`}
                className="absolute top-3 right-3 w-4 h-4 accent-red-500 z-10"
              />
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="label-eyebrow">{o.event_type}</div>
                </div>
                <div className="font-display text-xl mt-1.5">{o.customer_name}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <StatusBadge status={o.status} />
                <PaymentBadge order={o} />
              </div>
            </div>
            <div className="text-sm text-zinc-400">{o.package_name}</div>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {o.event_location}</span>
            </div>
            <div className="flex items-center justify-between text-sm pt-3 border-t border-[#1a1a1a]">
              <div className="flex items-center gap-2 text-zinc-400">
                <UsersIcon className="w-3.5 h-3.5" /> {o.pax} {t("common.pax")}
              </div>
              <div className="text-zinc-300">{fmtDay(o.event_date)}</div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">{t("common.total")}</span>
              <span className="text-[#D4AF37] font-medium">{fmtCurrency(o.total_amount)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-600">Tracking</span>
              <code className="text-[#D4AF37]">{o.tracking_code}</code>
            </div>
          </Link>
        ))}
      </div>

      <OrderFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); load(); }}
      />
    </div>
  );
}
